import queryString from "query-string";

import { referralRequests } from "../constants/actionTypes";
import { typeReferrals } from "../constants/referralRequests";
import apiClient from "../api/client";
import { normalizeImportReady } from "../helpers/referralImport";
import { toLoadError } from "../helpers/loadError";

// GROWTH-7288: counts list requests. Only the answer of the newest one reaches the store; an older request that is
// still in flight when a newer one starts (a page click during a slow load, two quick clicks) is stale, and its answer,
// its failure and its loading-off are dropped. The request is not cancelled: the request config carries no `signal`.
let listGeneration = 0;
// GROWTH-8109: the same guard for My Referrals v2's own list request.
let requesterGeneration = 0;

export const actions = {
  // GROWTH-6554: personal referral requests are never a team surface — always own account.
  /**
   * Loads one page of the member's referral requests.
   *
   * @param {Object} [params]
   * @param {string} [params.sort] sort key; left out of the request when empty
   * @param {number|string} [params.page] 0-based UI page; the request sends it plus one
   * @param {number[]} [params.dirId] directory filter
   * @param {number[]} [params.practiceId] practice filter
   * @param {string} [params.search] search text
   * @param {number} [params.perPage] page size; the server default applies when omitted
   * @param {"referrals"|"paid-referrals"|"all"} [params.type="referrals"] list tab
   * @param {boolean} [params.isSelectedFilter] passed through to the GET_CASES_SUCCESS payload
   * @returns {function(Function): void} thunk
   * @throws {TypeError} when params is null, an array or not an object (GROWTH-6566)
   */
  getCases(params = {}) {
    // GROWTH-6566: destructuring a scalar, array or null (e.g. getCases(searchParams.page)) would silently
    // load the default view, so fail loudly instead.
    if (params === null || typeof params !== "object" || Array.isArray(params)) {
      throw new TypeError("getCases expects an options object");
    }

    const { sort, page, dirId, practiceId, search, perPage, type = "referrals", isSelectedFilter } = params;

    return dispatch => {
      const generation = ++listGeneration;
      const isStale = () => generation !== listGeneration;

      dispatch(this.setLoading(true));

      const typeReferralsString = typeReferrals[type];
      const searchValue = search ? { search } : {};
      const perPageValue = perPage ? { per_page: perPage } : {};
      const sortValue = sort ? { sort } : {};
      const apiPage = page != null && page !== "" ? Number(page) + 1 : undefined;
      const pageValue = apiPage != null ? { page: apiPage } : {};
      const filterParams = queryString.stringify(
        { dir_id: dirId, practice_id: practiceId, ...pageValue, ...sortValue, ...searchValue, ...perPageValue },
        { arrayFormat: "index" }
      );

      apiClient({
        method: "get",
        responseType: "json",
        url: `/members/api-v2/referrals/cases${filterParams ? `?${filterParams}` : ""}`,
        params: { tab: typeReferralsString },
        headers: {
          "Content-Type": "application/json"
        }
      })
        .then(res => {
          if (isStale()) {
            return;
          }

          const { data, pagination: paginationMeta } = res.data;
          // GROWTH-6586: a non-numeric search under 3 characters comes back as an empty list plus search_error.
          // GROWTH-7293: `matched` is the member's matched requests across every page of the tab; absent from an older API.
          const { cases, hidden, purchased, notpurchased, filters, matched, search_error: searchError } = data;

          dispatch({
            type: referralRequests.GET_CASES_SUCCESS,
            payload: {
              cases: cases || [],
              pagination: {
                current_page: data.current_page != null ? data.current_page : paginationMeta && paginationMeta.current_page,
                last_page: data.last_page != null ? data.last_page : paginationMeta && paginationMeta.total_pages,
                per_page: data.per_page != null ? data.per_page : paginationMeta && paginationMeta.records_per_page,
                total: data.total != null ? data.total : paginationMeta && paginationMeta.total_records
              },
              tabsCounter: {
                hidden,
                purchased,
                notpurchased
              },
              filters,
              matched,
              isSelectedFilter,
              searchError
            }
          });
        })
        // GROWTH-6580: the page shows this failure instead of an empty list, so keep only what it may show.
        .catch(err => {
          if (!isStale()) {
            dispatch({ type: referralRequests.GET_CASES_FAIL, payload: toLoadError(err) });
          }
        })
        .finally(() => {
          // The newer request still owns the loading overlay.
          if (!isStale()) {
            dispatch(this.setLoading(false));
          }
        });
    };
  },

  /**
   * GROWTH-8109: My Referrals v2's list (`requester_view=1`): the requester's requests with their status group, last change,
   * open follow-up questions and matched professionals, and the count of every group over all pages.
   *
   * @param {Object} params
   * @param {"all"|"attention"|"review"|"matched"|"closed"} [params.group="all"]
   * @param {"updated"|"attention"|"newest"|"oldest"} [params.order="updated"]
   * @param {string} [params.search]
   * @param {number|string} [params.page] 0-based UI page; the request sends it plus one
   * @param {number|string} [params.perPage]
   * @param {boolean} [params.silent] reload without the loading state (after a dialog changed a request)
   * @returns {function(Function): Promise<void>}
   */
  getRequesterCases({ group = "all", order = "updated", search, page, perPage, silent = false } = {}) {
    return dispatch => {
      const generation = ++requesterGeneration;
      const isStale = () => generation !== requesterGeneration;

      dispatch({ type: referralRequests.REQUESTER_CASES_REQUEST, payload: { silent } });

      const apiPage = page != null && page !== "" ? Number(page) + 1 : undefined;
      const params = {
        requester_view: 1,
        tab: "index",
        group,
        order,
        ...(search ? { search } : {}),
        ...(apiPage != null ? { page: apiPage } : {}),
        ...(perPage ? { per_page: perPage } : {})
      };

      return apiClient({
        method: "get",
        responseType: "json",
        url: "/members/api-v2/referrals/cases",
        params,
        headers: { "Content-Type": "application/json" }
      })
        .then(res => {
          if (isStale()) {
            return;
          }
          const { data } = res.data;
          dispatch({
            type: referralRequests.REQUESTER_CASES_SUCCESS,
            payload: {
              cases: data.cases || [],
              counts: data.counts || {},
              group: data.group || group,
              searchError: data.search_error || "",
              pagination: {
                currentPage: Number(data.current_page) || 1,
                lastPage: Number(data.last_page) || 1,
                perPage: Number(data.per_page) || Number(perPage) || 10,
                total: Number(data.total) || 0
              }
            }
          });
        })
        .catch(err => {
          if (!isStale()) {
            dispatch({ type: referralRequests.REQUESTER_CASES_FAIL, payload: toLoadError(err) });
          }
        });
    };
  },

  clearRequesterCases() {
    requesterGeneration += 1;
    return { type: referralRequests.REQUESTER_CLEAR };
  },

  /** Changes fields of one listed request without a reload (an answered request moves to In review). */
  patchRequesterCase(leadId, patch) {
    return { type: referralRequests.REQUESTER_CASE_PATCH, payload: { leadId, patch } };
  },

  /** Takes a request off the list while its delete waits for the Undo window (GROWTH-8109). */
  hideRequesterCase(leadId) {
    return { type: referralRequests.REQUESTER_CASE_HIDE, payload: { leadId } };
  },

  /** Puts a hidden request back where it was (Undo). */
  restoreRequesterCase(leadId) {
    return { type: referralRequests.REQUESTER_CASE_RESTORE, payload: { leadId } };
  },

  /** The delete went through: the request no longer needs to be remembered for Undo. */
  forgetRequesterCase(leadId) {
    return { type: referralRequests.REQUESTER_CASE_FORGET, payload: { leadId } };
  },

  /** DELETE /referrals/case: the same call as referralRequestDetail.handleRemove, but it reports the outcome. */
  deleteRequesterCase(leadId) {
    return () =>
      apiClient({
        method: "DELETE",
        url: "/members/api-v2/referrals/case",
        params: { lead_id: leadId },
        headers: { "Content-Type": "application/json" }
      }).then(
        () => true,
        () => false
      );
  },

  /** POST /referrals/case: the same call as referralRequestDetail.changeCaseData, but it reports the outcome. */
  saveRequesterCaseEdit(data) {
    return () =>
      apiClient({
        method: "post",
        url: "/members/api-v2/referrals/case",
        data: JSON.stringify(data),
        headers: { "Content-Type": "application/json" }
      }).then(
        () => true,
        () => false
      );
  },

  /** POST /referrals save_message: the same call as referralRequestDetail.sendMatchMessage, but it reports the outcome. */
  sendRequesterMatchMessage(leadId, listingId, message) {
    return () =>
      apiClient({
        method: "post",
        url: "/members/api-v2/referrals",
        headers: { "Content-Type": "application/json" },
        data: { command: "save_message", lead_id: leadId, listing_id: listingId, message }
      }).then(
        response => response?.data?.success === true && Number(response?.data?.data?.listing_message_id) > 0,
        () => false
      );
  },

  getImportReady() {
    return (dispatch, getState) => {
      const {
        referralRequests: { importReady, preloaders }
      } = getState();

      if (Object.keys(importReady || {}).length || preloaders.importReady) {
        return Promise.resolve();
      }

      dispatch(this.setLoadingImportReady(true));
      return apiClient
        .get("/members/api-v2/referrals/import-ready")
        .then(res => {
          const importReadyData = normalizeImportReady(res.data);
          if (!importReadyData) {
            throw new TypeError("Invalid referral import-ready response");
          }

          dispatch({
            type: referralRequests.GET_IMPORT_READY,
            payload: importReadyData
          });
        })
        .catch(err => {
          dispatch(this.setLoadingImportReady(false));
          dispatch({
            type: referralRequests.GET_IMPORT_FAIL,
            payload: err
          });
        });
    };
  },

  getImport() {
    return dispatch => {
      dispatch(this.setLoadingImport(true));

      apiClient
        .get("/members/api-v2/referrals/import")
        .then(res => {
          dispatch({
            type: referralRequests.GET_IMPORT,
            payload: res.data.data
          });
          dispatch(this.getCases());
        })
        .catch(err =>
          dispatch({
            type: referralRequests.GET_IMPORT_FAIL,
            payload: err
          })
        );
    };
  },

  createNewReferralRequest(referralRequest) {
    return dispatch => {
      dispatch(this.setLoading(true));
      apiClient({
        method: "post",
        url: "/members/api-v2/referrals/add",
        headers: { "Content-Type": "application/json" },
        data: JSON.stringify(referralRequest)
      })
        .then(res => {
          dispatch({
            type: referralRequests.GET_NEW_REFERRAL_REQUEST,
            payload: {
              newReferralRequest: res.data.data.lead,
              followups: res.data.data.followups
            }
          });
        })
        .catch(error => {
          console.error("createNewReferralRequest", error);
        })
        .finally(() => {
          dispatch(this.setLoading(false));
        });
    };
  },

  sendAnswersForQuestion(answers) {
    return dispatch => {
      dispatch(this.updateReferralRequest(false));
      dispatch(this.setLoading(true));
      apiClient({
        method: "patch",
        url: "/members/api-v2/referrals/add",
        headers: { "Content-Type": "application/json" },
        data: JSON.stringify(answers)
      })
        .then(res => {
          dispatch(this.updateReferralRequest(res.data.data.ok));
          dispatch(this.clearFollowups());
        })
        .catch(error => {
          console.error("sendAnswersForQuestion", error);
        })
        .finally(() => {
          dispatch(this.setLoading(false));
        });
    };
  },

  clearFollowups() {
    return {
      type: referralRequests.CLEAR_NEW_REFERRAL_REQUEST
    };
  },

  setLoading(status) {
    return {
      type: referralRequests.SET_CASES_PRELOADER,
      payload: status
    };
  },

  setLoadingImport(status) {
    return {
      type: referralRequests.SET_CASES_PRELOADER_IMPORT,
      payload: status
    };
  },

  setLoadingImportReady(status) {
    return {
      type: referralRequests.SET_IMPORT_READY_PRELOADER,
      payload: status
    };
  },

  toggleActivityOfModal({ modal, isActive }) {
    return {
      type: referralRequests.TOGGLE_ACTIVITY_OF_MODAL,
      payload: {
        modal,
        isActive
      }
    };
  },

  clearAllModals() {
    return {
      type: referralRequests.CLEAR_ALL_MODALS
    };
  },

  setClearReferrals() {
    return {
      type: referralRequests.SET_CLEAR_REFERRALS
    };
  },

  updateReferralRequest(isUpdate) {
    return {
      type: referralRequests.UPDATE_REFERRAL_REQUEST,
      payload: isUpdate
    };
  }
};
